import { Component, Input, OnInit, ViewChildren, QueryList, ElementRef, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { Subject, takeUntil, map } from 'rxjs';
import { BlockPeriodClass as BlockPeriod } from 'src/app/class/meal-plan/block-period.class';
import { MealPlanClass as MealPlan } from 'src/app/class/meal-plan/meal-plan.class';
import { MealTimeClass } from 'src/app/class/meal-plan/meal-time.class';
import { PlanPeriodClass } from 'src/app/class/meal-plan/plan-period.class';
import { IIngredientCategory } from 'src/app/interfaces/database/database.interface';
import { IngredientState } from 'src/app/models/ingredient';
import { MessageAlertService } from 'src/app/services/message-alert/message-alert.service';


@Component({
  selector: 'app-meal-timing-table',
  templateUrl: './meal-timing-table.component.html',
  styleUrls: ['./meal-timing-table.component.scss']
})
export class MealTimingTableComponent implements OnInit, OnDestroy {

  @Input() showExceptions: boolean = false;
  @Input() planPeriod: PlanPeriodClass = null;

  categories!: IIngredientCategory[];
  private destroy$ = new Subject<void>();
  @ViewChildren('editableInput') editableInputs!: QueryList<ElementRef>;

  constructor(
    private store: Store,
    private messageAlertService: MessageAlertService
  ) { }

  ngOnInit(): void {
    this.store.select(IngredientState.getIngredientCategories).pipe(
      takeUntil(this.destroy$)
    ).subscribe(categories => this.categories = categories);

    this.initMealTimeDistribution();
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTotalNutrient(categoryId: string): number {
    return this.planPeriod.mealTimes.reduce((sum, period) => sum + (period.metadata.nutrientTable[categoryId] || 0), 0);
  }


  addMealTime(): void {
    const mealTime = new MealTimeClass();
    mealTime.editMode = true;
    mealTime.title = 'New time';
    this.planPeriod.createMealTime(mealTime);
    this.initMealTimeDistribution();
  }


  editMealTime(index: number): void {
    this.exitEditMode();
    this.planPeriod.mealTimes[index].editMode = true;
  }

  saveMealTimeTitle(index: number): void {
    const mealTime = this.planPeriod.mealTimes[index];
    mealTime.editMode = !mealTime.title.trim();
    mealTime.valid = mealTime.title.trim() !== '';
  }

  exitEditMode(): void {
    this.planPeriod.mealTimes.forEach(period => {
      period.editMode = false;
      period.valid = true;
    });
  }

 
  private initMealTimeDistribution(): void {
    this.planPeriod.mealTimes.forEach(mealTime => {
      this.categories.forEach(category => mealTime.metadata.nutrientTable[category._id] = mealTime.metadata.nutrientTable[category._id] || 0);
    });
  }

  deleteMealTime(index: number): void {
    if (this.planPeriod.mealTimes.length === 1) {
      this.messageAlertService.createMessage('warning', 'Cannot delete the only meal time from a period');
      return;
    }
    this.planPeriod.mealTimes.splice(index, 1);
  }

  getAssignedPercentage(categoryId: string): number {
    const required = this.getRequiredAmount(categoryId);
    const percentage = required === 0 ? 0 : (this.getTotalNutrient(categoryId) / required) * 100;
    return Math.min(Math.round(percentage * 100) / 100, 100);
  }

  getCategoryStatus(categoryId: string): 'success' | 'exception' | 'normal' {
    return this.getAssignedPercentage(categoryId) === 100 ? 'success' : this.showExceptions ? 'exception' : 'normal';
  }

  getMaxValueForInput(categoryId: string, currentValue: number): number {
    const totalAssigned = this.getTotalNutrient(categoryId);
    const required = this.getRequiredAmount(categoryId);
    return required - totalAssigned + (currentValue || 0);
  }

  isSumValid(categoryId: string): boolean {
    return this.getTotalNutrient(categoryId) <= this.getRequiredAmount(categoryId);
  }

  getRequiredAmount(categoryId: string): number {
    return this.planPeriod?.metadata?.nutrientTable[categoryId] || 0;
  }
}
